import { useState, useEffect, useCallback } from 'react'
import {
  type Difficulty,
  type Operator,
  type NumberCard,
  type ExprToken,
  generateAllPuzzles,
  evalExpression,
  DIFFICULTY_CONFIG,
} from './numTargetData'

export type { Difficulty, Operator, NumberCard, ExprToken }

export interface RoundResult {
  cardsUsed: number
}

export interface NumTargetState {
  puzzles:       { cards: NumberCard[]; goal: number }[]
  currentRound:  number
  cards:         NumberCard[]
  goal:          number
  expression:    ExprToken[]
  currentResult: number | null
  openParens:    number
  moves:         number
  wrongSubmit:   boolean
  correctFlash:  boolean
  roundResults:  RoundResult[]
  startTime:     number | null
  endTime:       number | null
  gameComplete:  boolean
}

function initState(difficulty: Difficulty): NumTargetState {
  const puzzles = generateAllPuzzles(difficulty)
  const first   = puzzles[0]
  return {
    puzzles,
    currentRound:  0,
    cards:         first.cards,
    goal:          first.goal,
    expression:    [],
    currentResult: null,
    openParens:    0,
    moves:         0,
    wrongSubmit:   false,
    correctFlash:  false,
    roundResults:  [],
    startTime:     null,
    endTime:       null,
    gameComplete:  false,
  }
}

function needsNumber(expression: ExprToken[]): boolean {
  if (expression.length === 0) return true
  const last = expression[expression.length - 1]
  return last.type === 'operator' || last.type === 'paren-open'
}

function canAddOperator(expression: ExprToken[]): boolean {
  if (expression.length === 0) return false
  const last = expression[expression.length - 1]
  return last.type === 'number' || last.type === 'paren-close'
}

export function useNumTarget(difficulty: Difficulty) {
  const [state, setState] = useState<NumTargetState>(() => initState(difficulty))

  const resetGame = useCallback(() => setState(initState(difficulty)), [difficulty])

  // Clear wrong-submit shake after 650 ms
  useEffect(() => {
    if (!state.wrongSubmit) return
    const t = setTimeout(() => setState((prev) => ({ ...prev, wrongSubmit: false })), 650)
    return () => clearTimeout(t)
  }, [state.wrongSubmit])

  // Advance to next round after correct flash
  useEffect(() => {
    if (!state.correctFlash) return
    const t = setTimeout(() => {
      setState((prev) => {
        if (prev.gameComplete) return { ...prev, correctFlash: false }
        const nextRound = prev.currentRound + 1
        const next      = prev.puzzles[nextRound]
        return {
          ...prev,
          correctFlash:  false,
          currentRound:  nextRound,
          cards:         next.cards,
          goal:          next.goal,
          expression:    [],
          currentResult: null,
          openParens:    0,
        }
      })
    }, 700)
    return () => clearTimeout(t)
  }, [state.correctFlash])

  const tapCard = useCallback((cardId: number) => {
    setState((prev) => {
      if (prev.gameComplete || prev.correctFlash) return prev
      const card = prev.cards.find((c) => c.id === cardId)
      if (!card || card.inExpression) return prev
      if (!needsNumber(prev.expression)) return prev

      const newExpr: ExprToken[] = [
        ...prev.expression,
        { type: 'number', cardId, value: card.value },
      ]
      const newCards = prev.cards.map((c) =>
        c.id === cardId ? { ...c, inExpression: true } : c,
      )
      return {
        ...prev,
        expression:    newExpr,
        cards:         newCards,
        currentResult: evalExpression(newExpr),
        startTime:     prev.startTime ?? Date.now(),
      }
    })
  }, [])

  const tapOperator = useCallback((op: Operator) => {
    setState((prev) => {
      if (prev.gameComplete || prev.correctFlash) return prev
      if (!canAddOperator(prev.expression)) return prev
      const newExpr: ExprToken[] = [...prev.expression, { type: 'operator', op }]
      return { ...prev, expression: newExpr, currentResult: null }
    })
  }, [])

  const tapParen = useCallback((which: 'open' | 'close') => {
    setState((prev) => {
      if (prev.gameComplete || prev.correctFlash) return prev

      if (which === 'open') {
        if (!needsNumber(prev.expression)) return prev
        const newExpr: ExprToken[] = [...prev.expression, { type: 'paren-open' }]
        return { ...prev, expression: newExpr, currentResult: null, openParens: prev.openParens + 1 }
      }

      // close
      if (prev.openParens === 0) return prev
      const last = prev.expression[prev.expression.length - 1]
      if (!last || (last.type !== 'number' && last.type !== 'paren-close')) return prev
      const newExpr: ExprToken[] = [...prev.expression, { type: 'paren-close' }]
      const newOpen = prev.openParens - 1
      return {
        ...prev,
        expression:    newExpr,
        openParens:    newOpen,
        currentResult: newOpen === 0 ? evalExpression(newExpr) : null,
      }
    })
  }, [])

  const deleteLast = useCallback(() => {
    setState((prev) => {
      if (prev.gameComplete || prev.correctFlash || prev.expression.length === 0) return prev
      const last    = prev.expression[prev.expression.length - 1]
      const newExpr = prev.expression.slice(0, -1)
      let newCards  = prev.cards
      let newOpen   = prev.openParens

      if (last.type === 'number') {
        newCards = prev.cards.map((c) =>
          c.id === last.cardId ? { ...c, inExpression: false } : c,
        )
      } else if (last.type === 'paren-open') {
        newOpen = Math.max(0, newOpen - 1)
      } else if (last.type === 'paren-close') {
        newOpen = newOpen + 1
      }

      return {
        ...prev,
        expression:    newExpr,
        cards:         newCards,
        openParens:    newOpen,
        currentResult: newOpen === 0 ? evalExpression(newExpr) : null,
      }
    })
  }, [])

  const clearExpression = useCallback(() => {
    setState((prev) => {
      if (prev.gameComplete || prev.correctFlash) return prev
      return {
        ...prev,
        expression:    [],
        cards:         prev.cards.map((c) => ({ ...c, inExpression: false })),
        currentResult: null,
        openParens:    0,
      }
    })
  }, [])

  const submitAnswer = useCallback(() => {
    setState((prev) => {
      if (prev.gameComplete || prev.correctFlash || prev.expression.length === 0) return prev
      if (prev.openParens !== 0) return prev
      const last = prev.expression[prev.expression.length - 1]
      if (!last || (last.type !== 'number' && last.type !== 'paren-close')) return prev

      const result   = evalExpression(prev.expression)
      const newMoves = prev.moves + 1

      if (result !== null && result === prev.goal) {
        const cardsUsed    = prev.expression.filter((t) => t.type === 'number').length
        const newResults   = [...prev.roundResults, { cardsUsed }]
        const isLastRound  = prev.currentRound === prev.puzzles.length - 1
        const gameComplete = isLastRound
        return {
          ...prev,
          moves:        newMoves,
          correctFlash: true,
          roundResults: newResults,
          gameComplete,
          endTime:      gameComplete ? Date.now() : prev.endTime,
        }
      }

      return { ...prev, moves: newMoves, wrongSubmit: true }
    })
  }, [])

  const allowedOps  = DIFFICULTY_CONFIG[difficulty].ops
  const allowParens = DIFFICULTY_CONFIG[difficulty].allowParens
  const totalRounds = DIFFICULTY_CONFIG[difficulty].roundCount

  return {
    state, allowedOps, allowParens, totalRounds,
    resetGame, tapCard, tapOperator, tapParen,
    deleteLast, clearExpression, submitAnswer,
  }
}
